import { TokenProvider } from '~/hooks/auth/useMsalTokenProvider';
import { RequestParameters } from './backend-client';
import { BackendV2Client } from './backend-v2-client';
import { APIType } from './types';
import { handleError } from '~/utils/errors';

/**
 * Payload for `POST /v2/composite_cbct/`. Matches the
 * `CompositeCBCTRegistrationRequest` schema in the backend OpenAPI:
 * `cbct_image` and `ct_image` are dataset IDs returned from
 * `POST /v2/images`, not raw scan UIDs or file paths. `model_name`
 * is optional — the backend substitutes its default composite-CBCT
 * model when omitted.
 */
export type CreateCompositeCbctTaskPayload = {
    task_id: string;
    cbct_image: string;
    ct_image: string;
    model_name?: string;
};

/**
 * Composite CBCT v2 client. Shares the `/v2/images` dataset
 * endpoints and the polling / result / delete lifecycle with every
 * other v2 task family via `BackendV2Client`; only adds the
 * family-specific task creation method.
 */
export class CompositeCbctClient extends BackendV2Client {
    protected get taskPathSegment(): string {
        return 'composite_cbct';
    }

    constructor(
        baseURL: string,
        tokenProvider: TokenProvider,
        sessionID: string
    ) {
        // Composite CBCT shares the adapt+ / registration API surface
        // on the backend (see the OpenAPI notes on `/v2/composite_cbct`
        // that explicitly reference the adapt+ registration model), so
        // it uses the same APIType for legacy v1 plumbing (list-active-
        // models, labeling) that the base BackendClient still exposes.
        super(baseURL, tokenProvider, sessionID, APIType.REGISTRATION);
    }

    /**
     * Creates a composite-CBCT task with two pre-uploaded scan
     * datasets. POSTs `/v2/composite_cbct/` with
     * `CompositeCBCTRegistrationRequest` and returns the server's
     * task presentation (including the task id).
     */
    public async createCompositeCbctTask(
        request: RequestParameters,
        payload: CreateCompositeCbctTaskPayload
    ): Promise<unknown> {
        try {
            const params: Record<string, string> = {};
            params.sessionId = request.sessionId ?? this.sessionID;

            const url = this.envelopeURL('v2/composite_cbct/', params);

            const headers = {
                ...(await this.defaultHeaders()),
                'Content-Type': 'application/json',
                skip_dicom_validation: 'true',
            };

            const body = JSON.stringify(payload);

            const response = await fetch(url, {
                method: 'POST',
                headers,
                body,
            });

            if (!response.ok) {
                const text = await response.text();
                handleError(
                    new Error(
                        `Failed to create composite CBCT task: ${response.status}`
                    ),
                    text
                );
                return false;
            }

            return response.json();
        } catch (error) {
            handleError(error, 'Error creating composite CBCT task.');
            return false;
        }
    }
}
